// One leg of a shared trip, read-only: the leg's fields as plain rows (no
// form controls -- LogbookTrip's register) plus its route map. The map is
// TripGroupMap with a single leg rather than TripRouteMap: the group map is
// already an editor-free picture (no add-waypoint, no marker drag, no
// waypoint modal), so read-only needs nothing suppressed.

import { Suspense } from 'react';
import { lazyWithReload } from '../../utilities/lazyWithReload';
import { useParams } from 'react-router-dom';
import Table from 'react-bootstrap/Table';
import { DataCard, Spinner } from '../../components';
import { CardHeader } from '../../components/CardHeader';
import { urlForSharedTrip } from '../../fleet-shared/Urls.mjs';
import { routeFromTrip, hasPlacedWaypoints } from '../../fleet-shared/TripRoute.mjs';
import { useSharedTrip, sharedTripColumns, sharedCellValue } from './SharedTrip';
import SharedBackLink from './SharedBackLink';

// Lazy: the only import path here that pulls in maplibre-gl (~250 KB gz).
const TripGroupMap = lazyWithReload(() => import('../../components/map/TripGroupMap'), 'TripGroupMap');

const SharedTripLeg = () => {
  const { legId } = useParams();
  const { shareId, name, displayLegs, placeIndex } = useSharedTrip();

  const legs = displayLegs ?? [];
  const index = legs.findIndex((candidate) => candidate.id === legId);
  const leg = index >= 0 ? legs[index] : null;

  const backLink = (
    <SharedBackLink to={urlForSharedTrip(shareId)}>
      Back to itinerary
    </SharedBackLink>
  );

  if (!leg) {
    return (
      <>
        {backLink}
        <p className="text-center text-body-secondary py-5">
          This leg is not part of this trip.
        </p>
      </>
    );
  }

  const note = typeof leg.notes === 'string' ? leg.notes.trim() : '';
  const onMap = hasPlacedWaypoints(routeFromTrip(leg));

  return (
    <>
      {backLink}

      <DataCard
        header={<CardHeader text={`${name} — Leg ${index + 1} of ${legs.length}`} />}
        body={
          <Table className="align-middle mb-0">
            <tbody>
              {sharedTripColumns.map((field) => {
                const { text, title } = sharedCellValue(leg, field, placeIndex);
                return (
                  <tr key={field.key}>
                    <th scope="row" className="text-body-secondary" style={{ width: 140 }}>
                      {field.name}
                    </th>
                    <td className="entered-data" title={title}>{text}</td>
                  </tr>
                );
              })}
              {note && (
                <tr>
                  <th scope="row" className="text-body-secondary">Notes</th>
                  <td className="entered-data" style={{ whiteSpace: 'pre-wrap' }}>{note}</td>
                </tr>
              )}
            </tbody>
          </Table>
        }
      />

      {onMap && (
        <Suspense fallback={<Spinner />}>
          <TripGroupMap legs={[leg]} onSelectLeg={() => {}} />
        </Suspense>
      )}
    </>
  );
};

export default SharedTripLeg;
